import { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { HelpCircle, ChevronLeft, ChevronRight, X, Sparkles } from 'lucide-react';

interface TourStep {
  target: string;
  title: string;
  content: string;
  placement: 'center' | 'bottom' | 'top' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  {
    target: 'body',
    title: 'Welcome to DCode DEW 🌟',
    content: "Welcome to the Estimate Weaver dashboard! We've prepared a quick 1-minute tour to help you master the DCODE estimation ecosystem, generate premium estimates, issue sequential tax invoices, and track your double-entry accounting ledgers.",
    placement: 'center',
  },
  {
    target: '[data-tour="sidebar-nav"]',
    title: 'Central Control Hub 🎛️',
    content: "Here is your navigation center. Switch between the Dashboard pipeline, Quotation Library, Invoices Hub, Client Registry, double-entry Accounting Module, and workspace Settings.",
    placement: 'right',
  },
  {
    target: '[data-tour="new-quote-button"]',
    title: 'Initiate First Project 🚀',
    content: "Ready to estimate? Click 'New Estimate' to open the creation wizard. It seeds items, structures rooms/work packages, and handles sequential DCode quote numbering automatically.",
    placement: 'bottom',
  },
  {
    target: '[data-tour="search-registry"]',
    title: 'Registry Search Engine 🔍',
    content: "Search through your quotation records, client names, or project names instantly. You can also press Ctrl+K at any time to open the Global Command HUD for rapid actions.",
    placement: 'bottom',
  },
  {
    target: '[data-tour="guide-link"]',
    title: 'DCODE Knowledge Base 📚',
    content: "Have questions about GST calculations, payment milestones, or custom room formulas? Check the built-in Guide at any time for detailed documentation.",
    placement: 'right',
  }
];

export function AppTour() {
  const [activeStep, setActiveStep] = useState<number | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  // Listen to manual start event
  useEffect(() => {
    const handleStartTour = () => {
      setActiveStep(0);
    };

    window.addEventListener('start-app-tour', handleStartTour);
    return () => window.removeEventListener('start-app-tour', handleStartTour);
  }, []);

  // Check if tour should start automatically for first-time users
  useEffect(() => {
    const tourCompleted = localStorage.getItem('dew-tour-completed');
    if (tourCompleted !== 'true') {
      const timer = setTimeout(() => {
        setActiveStep(0);
      }, 1500); // Trigger 1.5s after mount
      return () => clearTimeout(timer);
    }
  }, []);

  // Update spotlight rect when step changes or window resizes
  useEffect(() => {
    if (activeStep === null) {
      setTargetRect(null);
      return;
    }

    const step = TOUR_STEPS[activeStep];
    if (!step || step.target === 'body') {
      setTargetRect(null);
      return;
    }

    const updateRect = () => {
      const element = document.querySelector(step.target);
      if (element) {
        // Ensure element is visible and scroll into view if needed
        element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        const rect = element.getBoundingClientRect();
        setTargetRect(rect);
      } else {
        setTargetRect(null);
      }
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    // Minor delay to let any active transitions settle
    const timer = setTimeout(updateRect, 300);

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      clearTimeout(timer);
    };
  }, [activeStep]);

  const handleNext = () => {
    if (activeStep === null) return;
    if (activeStep < TOUR_STEPS.length - 1) {
      setActiveStep(activeStep + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (activeStep === null) return;
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleSkip = () => {
    handleComplete();
  };

  const handleComplete = () => {
    setActiveStep(null);
    localStorage.setItem('dew-tour-completed', 'true');
  };

  if (activeStep === null) return null;

  const currentStep = TOUR_STEPS[activeStep];
  const isCenter = currentStep.placement === 'center' || !targetRect;

  // Calculate tooltip placement styles dynamically relative to the spotlighted rect
  const getTooltipStyles = () => {
    if (isCenter || !targetRect) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'fixed' as const,
      };
    }

    const gap = 16;
    const { top, left, width, height } = targetRect;

    switch (currentStep.placement) {
      case 'bottom':
        return {
          top: `${top + height + gap}px`,
          left: `${left + width / 2}px`,
          transform: 'translateX(-50%)',
          position: 'fixed' as const,
        };
      case 'top':
        return {
          top: `${top - gap}px`,
          left: `${left + width / 2}px`,
          transform: 'translate(-50%, -100%)',
          position: 'fixed' as const,
        };
      case 'right':
        return {
          top: `${top + height / 2}px`,
          left: `${left + width + gap}px`,
          transform: 'translateY(-50%)',
          position: 'fixed' as const,
        };
      case 'left':
        return {
          top: `${top + height / 2}px`,
          left: `${left - gap}px`,
          transform: 'translate(-100%, -50%)',
          position: 'fixed' as const,
        };
      default:
        return {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          position: 'fixed' as const,
        };
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none select-none">
      {/* Background Mask / Dimmer */}
      <div 
        className="absolute inset-0 bg-black/60 pointer-events-auto transition-opacity duration-300"
        onClick={handleSkip}
      />

      {/* Spotlight highlight */}
      {targetRect && (
        <div 
          className="fixed z-[10000] border-2 border-emerald-500 rounded-xl transition-all duration-300 pointer-events-none"
          style={{
            top: `${targetRect.top - 6}px`,
            left: `${targetRect.left - 6}px`,
            width: `${targetRect.width + 12}px`,
            height: `${targetRect.height + 12}px`,
            boxShadow: '0 0 0 9999px rgba(3, 3, 5, 0.75), 0 0 20px rgba(16, 185, 129, 0.3)',
          }}
        />
      )}

      {/* Tooltip Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeStep}
          ref={tooltipRef}
          initial={{ opacity: 0, scale: 0.95, y: isCenter ? -40 : 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed z-[10001] w-[340px] pointer-events-auto select-text
            bg-popover/95 backdrop-blur-md border border-border rounded-2xl p-5
            shadow-[0_20px_50px_rgba(0,0,0,0.8)] overflow-hidden"
          style={getTooltipStyles()}
        >
          {/* Subtle top decoration */}
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-emerald-500 via-teal-500 to-transparent" />
          
          <div className="flex items-start justify-between gap-4 mb-3">
            <h4 className="text-sm font-semibold text-foreground uppercase tracking-tight flex items-center gap-1.5 leading-none">
              {currentStep.title}
            </h4>
            <button 
              onClick={handleSkip}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <p className="text-muted-foreground text-xs font-semibold leading-relaxed mb-5">
            {currentStep.content}
          </p>

          <div className="flex items-center justify-between gap-3 pt-3 border-t border-border">
            <div className="flex items-center gap-1">
              {TOUR_STEPS.map((_, i) => (
                <div 
                  key={i} 
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === activeStep ? 'w-4 bg-emerald-500' : 'w-1.5 bg-slate-800'
                  }`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              {activeStep > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleBack}
                  className="h-8 px-2 text-muted-foreground hover:text-foreground hover:bg-muted/50 font-semibold uppercase text-[9px] tracking-widest"
                >
                  <ChevronLeft className="h-3.5 w-3.5 mr-0.5" /> Back
                </Button>
              )}
              <Button 
                size="sm"
                onClick={handleNext}
                className="h-8 px-3 rounded-lg font-semibold uppercase tracking-widest text-[9px] bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                {activeStep === TOUR_STEPS.length - 1 ? 'Finish' : 'Next'} <ChevronRight className="h-3.5 w-3.5 ml-0.5" />
              </Button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
